/**
 * Admin → Scopes.
 *
 * Full CRUD over the group-like scope types (teams + projects) and their
 * members. "New team / project" opens the create modal; row click opens
 * `ScopeGroupDrawer` — the management surface (rename, members, delete).
 * Dual surface with the confirmation-gated `scope_*` MCP tools — both hit
 * the same server-side invariants.
 */
import { useState } from "react";

import { adminApi, type AdminScopeGroupType } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { QueryBoundary } from "../../components/QueryBoundary";
import { FilterChips } from "../../components/ui/FilterChips";
import { formatRelative } from "../../lib/formatRelative";
import { ScopeGroupCreateModal } from "./ScopeGroupCreateModal";
import { ScopeGroupDrawer } from "./ScopeGroupDrawer";

const TABS: { value: AdminScopeGroupType; label: string }[] = [
  { value: "team", label: "Teams" },
  { value: "project", label: "Projects" },
];

export function ScopesPage() {
  const [tab, setTab] = useState<AdminScopeGroupType>("team");
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const groupsQuery = adminApi.scopes.groups(tab);

  return (
    <>
      <PageHeader
        title="Scopes"
        description="Group-like scopes (teams and projects) and their members. Also manageable via Jarvis (scope_* tools)."
        actions={
          <button
            type="button"
            className="btn btn-main"
            onClick={() => setCreateOpen(true)}
          >
            {tab === "team" ? "New team" : "New project"}
          </button>
        }
      />

      <div className="flex items-center gap-2 mb-5">
        <FilterChips
          aria-label="Scope type"
          options={TABS}
          value={tab}
          onChange={(value) => {
            setTab(value);
            setOpenGroupId(null);
          }}
        />
        <div className="text-sm text-ink-low ml-2">
          <span className="tnum font-medium text-ink">
            {groupsQuery.data?.length ?? 0}
          </span>{" "}
          listed
        </div>
      </div>

      <QueryBoundary
        query={groupsQuery}
        empty={
          <div className="empty-state">
            <div className="title">No {tab === "team" ? "teams" : "projects"}</div>
            <p className="hint">
              Create one with "{tab === "team" ? "New team" : "New project"}"
              above, or via Jarvis: scope_create(scope_type="{tab}", …).
            </p>
          </div>
        }
        isEmpty={(data) => data.length === 0}
      >
        {(groups) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Members</th>
                  <th>Description</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr
                    key={group.id}
                    onClick={() => setOpenGroupId(group.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className="font-heading font-semibold text-ink">
                        {group.name}
                      </span>
                    </td>
                    <td>
                      <span className="tag-chip">{group.slug}</span>
                    </td>
                    <td className="text-sm tnum">{group.member_count}</td>
                    <td className="text-sm text-ink-medium">
                      {group.description || "—"}
                    </td>
                    <td className="text-sm text-ink-low tnum">
                      {group.created_at ? formatRelative(group.created_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>

      <ScopeGroupCreateModal
        open={createOpen}
        typeName={tab}
        onClose={() => setCreateOpen(false)}
        onCreated={(groupId) => {
          setCreateOpen(false);
          setOpenGroupId(groupId);
        }}
      />

      <ScopeGroupDrawer
        typeName={tab}
        groupId={openGroupId}
        onClose={() => setOpenGroupId(null)}
      />
    </>
  );
}
